import * as focusTrap from "focus-trap";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleCloseButton } from "../Header/buttons/CircleCloseButton";
import { useTranslations } from "../i18n/useTranslations";
import { useAdminBar } from "../Nav/useAdminBar";
import { useUser } from "../user/useUser";
import { mapBooleanToOpenClose } from "../utils";
import { CategoryBlock } from "./CategoryBlock";
import { DDProBlock } from "./DDProBlock";
import { FreeTrialAndLogin } from "./FreeTrialAndLogin";
import { Logout } from "./Logout";
import { NewsSupportAbout } from "./NewsSupportAbout";
import { StoreBlock } from "./store/StoreBlock";
import { useMenu } from "./useMenu";

export function Menu() {
  const { t } = useTranslations();
  const { loggedIn } = useUser();
  const { closeMenu, isOpen, items } = useMenu();
  const { hasAdminBar } = useAdminBar();
  const [, categories] = items.all;
  const navRef = useRef<HTMLElement>();
  const navInnerRef = useRef<HTMLDivElement>();
  const closeBtn = useRef<HTMLButtonElement>();
  const [scrollHasReachEnd, setScrollHasReachEnd] = useState(false);
  const innerNavStyles = useMemo(
    () => (scrollHasReachEnd ? "elm-opacity-0" : "elm-opacity-100"),
    [scrollHasReachEnd]
  );

  useEffect(() => {
    const el = navRef.current;
    // scroll the menu to the top when closed
    // next time the user doesn't need to scroll up
    el?.scrollTo?.(0, 0);
  }, [isOpen]);

  useEffect(() => {
    const el = navRef.current;

    el.classList.toggle("menu-open", isOpen);
    el.classList.toggle("menu-close", !isOpen);
    el.classList.toggle("md:elm-block", isOpen);
    el.classList.toggle("elm-hidden", !isOpen);
    el.classList.toggle("md:elm-hidden", !isOpen);
  }, [isOpen]);

  const onClickOnAnyLink = useCallback(() => {
    navRef.current?.scroll?.({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const onChildOpen = useCallback((isChildOpen: boolean) => {
    navRef.current.classList.toggle("elm-invisible", !isChildOpen);
  }, []);

  useEffect(() => {
    if (hasAdminBar === true) {
      const el = navRef.current;
      el.classList.add("elm-mt-7");
    }
  }, [hasAdminBar]);

  useEffect(() => {
    if (isOpen && navRef.current !== null) {
      const trap = focusTrap
        .createFocusTrap(navRef.current, {
          fallbackFocus: closeBtn.current,
          allowOutsideClick: true
        })
        .activate();
      return () => void trap.deactivate();
    }
  }, [isOpen]);

  useEffect(() => {
    const ob = new IntersectionObserver(payload => {
      setScrollHasReachEnd(payload[0].isIntersecting);
    });

    const c = navInnerRef.current;
    ob.observe(c.lastElementChild);

    return () => {
      ob.unobserve(c.lastElementChild);
    };
  }, [navInnerRef]);

  return (
    <>
      <nav
        ref={navRef}
        tabIndex={-1}
        aria-label={t("Menu")}
        data-state={mapBooleanToOpenClose(isOpen)}
        className="elm-hidden md:elm-hidden elm-fixed elm-z-10 md:elm-z-30 elm-overflow-y-auto elm-h-full md:elm-max-w-md elm-min-h-screen elm-top-0 elm-w-full elm-bg-white elm-select-none"
      >
        <div ref={navInnerRef} className="md:elm-pl-20 md:elm-pr-10 elm-pt-14 elm-p-4 elm-pb-24">
          <button
            ref={closeBtn}
            data-track-label="btn:menu:close"
            className={`focus:elm-outline-none elm-visible elm-p-0 elm-border-0 elm-bg-transparent elm-hidden md:elm-block elm-z-20 elm-absolute elm-top-2 elm-left-3 elm-w-10 elm-h-10 elm-rounded-full elm-overflow-hidden elm-cursor-pointer elm-transition lg:hover:elm-bg-dd-blue-100`}
            onClick={closeMenu}
            title={t("Close Menu")}
            aria-label={t("Close Menu")}
          >
            <CircleCloseButton />
          </button>
          <div
            role="heading"
            className="elm-font-semibold elm-m-0 elm-ml-1 elm-mt-8 elm-mb-2 elm-text-3xl elm-text-dd-gray-900"
          >
            {categories.label}
          </div>
          <DDProBlock onClickOnAnyLink={onClickOnAnyLink} onChildOpen={onChildOpen} />
          <CategoryBlock onClickOnAnyLink={onClickOnAnyLink} onChildOpen={onChildOpen} />
          <FreeTrialAndLogin />
          <NewsSupportAbout onClickOnAnyLink={onClickOnAnyLink} onChildOpen={onChildOpen} />
          {loggedIn && <Logout />}
          <StoreBlock />
        </div>
        <div
          className={`${innerNavStyles} elm-transition-opacity elm-duration-500 md:elm-transition-none elm-bg-gradient-to-b elm-from-transparent elm-to-black-opacity-30 elm-w-full elm-h-24 elm-sticky elm-bottom-0 md:elm--bottom-12 elm-left-0`}
        ></div>
      </nav>
    </>
  );
}
